import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Wrench } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { MarkdownPreview } from '../ui/MarkdownPreview';

export interface ConversationMessage {
  id?: string;
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  timestamp?: string;
  stage?: string;
  toolName?: string;
  toolInput?: string;
  toolOutput?: string;
  durationMs?: number;
}

interface ConversationViewProps {
  messages: ConversationMessage[];
  emptyLabel?: string;
}

function formatTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

export function formatConversationMessageContent(content: string): string {
  const text = String(content || '');
  if (!text.includes('\\n')) return text;
  return text.replace(/\\n/g, '\n');
}

export function ConversationView({ messages, emptyLabel = 'No conversation captured.' }: ConversationViewProps) {
  const [openSystemIds, setOpenSystemIds] = useState<Record<string, boolean>>({});
  const [openToolIds, setOpenToolIds] = useState<Record<string, boolean>>({});

  const normalized = useMemo(() => {
    return (messages || []).map((message, index) => ({
      ...message,
      id: message.id || `${message.role}-${index}`,
      content: formatConversationMessageContent(String(message.content || '')),
      toolInput: message.toolInput ? String(message.toolInput) : undefined,
      toolOutput: message.toolOutput ? String(message.toolOutput) : undefined,
    }));
  }, [messages]);

  if (normalized.length === 0) {
    return (
      <div className="rounded border border-dashed border-gray-300 p-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {normalized.map((message) => {
        const isSystem = message.role === 'system';
        const isUser = message.role === 'user';
        const isTool = message.role === 'tool';
        const id = message.id as string;
        const systemOpen = Boolean(openSystemIds[id]);
        const toolOpen = Boolean(openToolIds[id]);

        if (isTool) {
          return (
            <div key={id} className="rounded border border-amber-200 bg-amber-50/70 dark:border-amber-900/40 dark:bg-amber-950/20">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-900 dark:text-amber-200"
                onClick={() => setOpenToolIds((prev) => ({ ...prev, [id]: !toolOpen }))}
              >
                {toolOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Wrench className="h-4 w-4" />
                <span>{message.toolName || 'Tool Call'}</span>
                {message.durationMs ? (
                  <span className="ml-auto text-xs text-amber-700 dark:text-amber-400">{message.durationMs} ms</span>
                ) : null}
              </button>
              {toolOpen && (
                <div className="space-y-3 border-t border-amber-200 px-3 py-3 dark:border-amber-900/40">
                  {message.toolInput && (
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-400">Input</p>
                      <pre className="max-h-48 overflow-auto rounded bg-white p-2 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                        {message.toolInput}
                      </pre>
                    </div>
                  )}
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-400">Output</p>
                    <pre className="max-h-72 overflow-auto rounded bg-white p-2 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                      {message.toolOutput || message.content}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          );
        }

        const bubbleClass = isSystem
          ? 'border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/40'
          : isUser
            ? 'ml-auto max-w-[90%] border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30'
            : 'mr-auto max-w-[95%] border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900/50';

        return (
          <div key={id} className={`rounded border p-3 ${bubbleClass}`}>
            <div className="mb-2 flex items-center gap-2 text-xs">
              <Badge variant={isSystem ? 'outline' : isUser ? 'default' : 'success'}>
                {isSystem ? 'System' : isUser ? 'User' : 'Assistant'}
              </Badge>
              {message.stage ? <span className="text-gray-500 dark:text-gray-400">{message.stage}</span> : null}
              {message.timestamp ? <span className="ml-auto text-gray-500 dark:text-gray-400">{formatTime(message.timestamp)}</span> : null}
            </div>
            {isSystem ? (
              <>
                <button
                  type="button"
                  className="mb-2 inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400"
                  onClick={() => setOpenSystemIds((prev) => ({ ...prev, [id]: !systemOpen }))}
                >
                  {systemOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  {systemOpen ? 'Hide system prompt' : 'Show system prompt'}
                </button>
                {systemOpen ? <MarkdownPreview content={message.content} /> : null}
              </>
            ) : (
              <MarkdownPreview content={message.content} />
            )}
          </div>
        );
      })}
    </div>
  );
}
